'use client';

import { API_ROUTES, ApiError, useApiCall, useApiQueryWithHeaders } from '@shared/api-utils';
import { useSignIn } from '@shared/auth';
import { useMutation } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { useEffect, useEffectEvent } from 'react';
import type { PlayerAddressData, ReKycRequest } from './types';

function useReKyc({ enabled }: { enabled: boolean }) {
  const query = useApiQueryWithHeaders<PlayerAddressData>(API_ROUTES.MY_ACCOUNT.PLAYER_AUTH.REKYC_ADDRESS_GET, {
    credentials: 'include',
    // NOTE: The API may respond with a redirect if the JWT has expired.
    // It doesn't make sense for this redirect to be followed as an API consumer, so we need to intercept and handle manually.
    redirect: 'manual',
    queryOptions: {
      enabled,
    },
  });

  const pathname = usePathname();
  const signIn = useSignIn();
  const handleJwtExpiry = () => signIn({ callbackUrl: pathname });
  const onJwtExpiry = useEffectEvent(handleJwtExpiry);

  useEffect(() => {
    if (!query.error || !(query.error instanceof ApiError)) return;

    if (query.error.getStatus() === 401) {
      onJwtExpiry();
    }
  }, [query.error]);

  const apiCall = useApiCall();

  // The API confusingly responds with a redirect if login is successful, but we have an additional screen to display
  const treatRedirectAsSuccess = (error: unknown) => {
    if (!(error instanceof ApiError)) throw error;

    if (error.getResponse().type === 'opaqueredirect') {
      return { success: true };
    }

    throw error;
  };

  const onMutationError = (error: unknown) => {
    if (error instanceof ApiError && error.getStatus() === 401) {
      handleJwtExpiry();
    }
  };

  const updateMutation = useMutation({
    mutationFn: (data: ReKycRequest) =>
      apiCall<unknown>(API_ROUTES.MY_ACCOUNT.PLAYER_AUTH.REKYC_ADDRESS_UPDATE_PUT, 'PUT', {
        body: data,
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        redirect: 'manual',
      }).catch(treatRedirectAsSuccess),
    onError: onMutationError,
  });

  const confirmMutation = useMutation({
    mutationFn: () =>
      apiCall<unknown>(API_ROUTES.MY_ACCOUNT.PLAYER_AUTH.REKYC_ADDRESS_CONFIRM_PUT, 'PUT', {
        body: {},
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        redirect: 'manual',
      }).catch(treatRedirectAsSuccess),
    onError: onMutationError,
  });

  return { query, updateMutation, confirmMutation };
}

export { useReKyc };
